import { useMutation, useQuery } from '@apollo/client/react';
import {
  AddOutlined,
  ConstructionOutlined,
  DeleteOutline,
  EditOutlined,
  EngineeringOutlined,
  MenuBookOutlined,
  PeopleOutline,
} from '@mui/icons-material';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { EssealDataTable } from 'esseal-data-table';
import { filter, find, map, some } from 'lodash';
import React, { useContext, useState } from 'react';

import DiaryEntryModal from '../ReportComponents/DiaryEntry/DiaryEntryModal';
import ConflictDetectedModal from '../ReportComponents/PeopleRegister/ConflictDetectedModal';
import RequestPermissionTooltip from '../RequestPermissionTooltip';

import {
  diaryEntryColumnsV2,
  equipmentRegisterColumnsV2,
  peopleRegisterColumnsV2,
  subContractorRegisterColumnsV2,
} from './corporateColumns';

import DeliveryIcon from '@/assets/DeliveryIcon';
import EditScheduleModal from '@/components/Scheduling/EditScheduleModal';
import NewScheduleModal from '@/components/Scheduling/NewScheduleModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  CompanyResourceResourceTypeEnum,
  type CreateSingleScheduleMutation,
  EmploymentStatusEnum,
  GetDeliveryItemStatesDocument,
  GetReportIssuesDocument,
  GetSchedulesDocument,
  type Issue,
  MarkAsReadyForCollectionDocument,
  PermissionActionEnum,
  PermissionEntityEnum,
  RemoveIssueDocument,
  ResourceScheduleStatusEnum,
  type Schedule,
  UpdateReportIssueDocument,
  UpdateScheduleDocument,
} from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

export type RegisterTabKey = 'people' | 'equipment' | 'subcontractors' | 'diary';

interface UnifiedRegistersCardV2Props {
  activeTab: RegisterTabKey;
  onTabChange: (tab: RegisterTabKey) => void;
  issues: Issue[];
  onSelectDiaryIssue: (issue: Issue) => void;
}

const UnifiedRegistersCardV2: React.FC<UnifiedRegistersCardV2Props> = ({
  activeTab,
  onTabChange,
  issues,
  onSelectDiaryIssue,
}) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { selectedProject, selectedReport, projectShifts, projectHolidays } = useContext(ReportContext);
  const { can, canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);

  const [openScheduleModalType, setOpenScheduleModalType] = useState<CompanyResourceResourceTypeEnum | null>(null);
  const [openDiaryModal, setOpenDiaryModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [conflictingId, setConflictingId] = useState<string | null>(null);

  // 1. People Schedules Query
  const { data: peopleData, loading: loadingPeople } = useQuery(GetSchedulesDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !selectedReport,
    variables: {
      date: selectedReport?.reportDate as string,
      projectId: selectedProject?.id as string,
      resourceType: CompanyResourceResourceTypeEnum.Human,
    },
  });

  // 2. Equipment Schedules Query
  const { data: equipmentData, loading: loadingEquipment } = useQuery(GetSchedulesDocument, {
    skip: !selectedReport,
    variables: {
      projectId: selectedReport?.projectId as string,
      date: selectedReport?.reportDate as string,
      resourceType: CompanyResourceResourceTypeEnum.Equipment,
    },
  });

  // 3. Subcontractor Schedules Query
  const { data: subContractorData, loading: loadingSubContractors } = useQuery(GetSchedulesDocument, {
    skip: !selectedReport,
    variables: {
      projectId: selectedReport?.projectId as string,
      date: selectedReport?.reportDate as string,
      resourceType: CompanyResourceResourceTypeEnum.SubContractor,
    },
  });

  const peopleRows = (peopleData?.getSchedules as Schedule[]) || [];
  const equipmentRows = filter(
    (equipmentData?.getSchedules as Schedule[]) || [],
    (s) => s.companyResource?.resourceType === CompanyResourceResourceTypeEnum.Equipment,
  );
  const subContractorRows = filter(
    (subContractorData?.getSchedules as Schedule[]) || [],
    (s) => s.companyResource?.resourceType === CompanyResourceResourceTypeEnum.SubContractor,
  );

  const [updateSchedule] = useMutation(UpdateScheduleDocument);
  const [markAsReadyForCollection] = useMutation(MarkAsReadyForCollectionDocument, {
    onCompleted: () => {
      displayNotification({
        text: 'Equipment marked as ready for collection successfully',
        type: 'success',
      });
    },
    onError: (error) => {
      displayNotification({ text: error.message, type: 'error' });
    },
    refetchQueries: [
      {
        query: GetSchedulesDocument,
        variables: {
          projectId: selectedReport?.projectId,
          date: selectedReport?.reportDate,
          resourceType: CompanyResourceResourceTypeEnum.Equipment,
        },
      },
    ],
  });

  const [removeIssue] = useMutation(RemoveIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: { reportId: selectedReport?.id as string },
      },
    ],
  });

  const [updateIssue] = useMutation(UpdateReportIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: { reportId: selectedReport?.id as string },
      },
    ],
  });

  const handleDeleteSchedule = (id: string, resourceType: CompanyResourceResourceTypeEnum, label: string) => {
    if (
      !can(PermissionActionEnum.Manage, PermissionEntityEnum.Scheduling, {
        projectId: selectedProject?.id,
      })
    )
      return;
    if (!selectedReport || !selectedProject) return;

    updateSchedule({
      variables: {
        schedule: {
          id,
          status: ResourceScheduleStatusEnum.Dropped,
        },
      },
      refetchQueries: [
        {
          query: GetSchedulesDocument,
          variables: {
            projectId: selectedProject.id,
            date: selectedReport.reportDate,
            resourceType,
          },
        },
      ],
      onCompleted: () =>
        displayNotification({
          type: 'success',
          text: `${label} schedule removed successfully`,
        }),
      onError: (error) => displayNotification({ type: 'error', text: error.message }),
    });
  };

  const handleReadyForCollection = (id: string, companyResourceId: string) => {
    if (
      !can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, {
        projectId: selectedProject?.id,
      })
    )
      return;

    markAsReadyForCollection({
      variables: {
        scheduleId: id,
        companyResourceId,
        projectId: selectedReport?.projectId as string,
        reportId: selectedReport?.id as string,
      },
      update(cache, { data }) {
        const updatedState = data?.markAsReadyForCollection;
        const reportId = selectedReport?.id;
        if (!reportId || !updatedState) return;

        const existingItems =
          cache.readQuery({
            query: GetDeliveryItemStatesDocument,
            variables: { reportId },
          })?.getDeliveryItemStates ?? [];

        cache.writeQuery({
          query: GetDeliveryItemStatesDocument,
          variables: { reportId },
          data: {
            getDeliveryItemStates: some(existingItems, (item) => item.item.id === updatedState.item.id)
              ? map(existingItems, (item) => (item.item.id === updatedState.item.id ? updatedState : item))
              : [...existingItems, updatedState],
          },
        });
      },
    });
  };

  const handleUpdateDiaryInline = (
    value: string,
    field: 'startTime' | 'endTime' | 'issueType' | 'description',
    id: string,
  ) => {
    let formattedValue = value;
    if (field === 'startTime' || field === 'endTime') {
      const currentDate = new Date();
      const [hours, minutes] = value.split(':').map(Number);
      const updatedDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        hours,
        minutes,
      );
      formattedValue = updatedDate.toISOString();
    }

    updateIssue({
      variables: {
        reportIssue: {
          id,
          [field]: formattedValue,
        },
      },
    });
  };

  if (!selectedReport) return <></>;

  const tabs: Array<{
    key: RegisterTabKey;
    label: string;
    count: number;
    icon: React.ReactNode;
    addButtonLabel: string;
    permissionEntity: PermissionEntityEnum;
  }> = [
    {
      key: 'people',
      label: 'People Register',
      count: peopleRows.length,
      icon: <PeopleOutline sx={{ fontSize: 16 }} />,
      addButtonLabel: 'Add Person',
      permissionEntity: PermissionEntityEnum.Scheduling,
    },
    {
      key: 'equipment',
      label: 'Equipment Register',
      count: equipmentRows.length,
      icon: <ConstructionOutlined sx={{ fontSize: 16 }} />,
      addButtonLabel: 'Add Equipment',
      permissionEntity: PermissionEntityEnum.Scheduling,
    },
    {
      key: 'subcontractors',
      label: 'Subcontractors',
      count: subContractorRows.length,
      icon: <EngineeringOutlined sx={{ fontSize: 16 }} />,
      addButtonLabel: 'Add Subcontractor',
      permissionEntity: PermissionEntityEnum.Scheduling,
    },
    {
      key: 'diary',
      label: 'Diary Entries Log',
      count: issues.length,
      icon: <MenuBookOutlined sx={{ fontSize: 16 }} />,
      addButtonLabel: 'Add Diary Entry',
      permissionEntity: PermissionEntityEnum.Events,
    },
  ];

  const currentTabConfig = find(tabs, (t) => t.key === activeTab) || tabs[0];

  const hasAddPermission =
    activeTab === 'diary'
      ? canPerformAction(PermissionActionEnum.Create, PermissionEntityEnum.Events)
      : can(PermissionActionEnum.Create, PermissionEntityEnum.Scheduling, {
          projectId: selectedProject?.id,
        });

  const handlePrimaryAddClick = () => {
    if (!hasAddPermission) return;
    if (activeTab === 'people') {
      setOpenScheduleModalType(CompanyResourceResourceTypeEnum.Human);
    } else if (activeTab === 'equipment') {
      setOpenScheduleModalType(CompanyResourceResourceTypeEnum.Equipment);
    } else if (activeTab === 'subcontractors') {
      setOpenScheduleModalType(CompanyResourceResourceTypeEnum.SubContractor);
    } else {
      setOpenDiaryModal(true);
    }
  };

  const timeFormat = (dateTimePreference.time as string) || 'HH:mm';

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 2,
        overflow: 'hidden',
        '.scheduled-row': {
          '.dg-cell': {
            backgroundColor: '#FFF1F2',
            borderColor: '#FECDD3',
          },
          '&:hover .dg-cell': {
            backgroundColor: '#FFE4E6',
          },
        },
      }}
    >
      {/* Unified Register Header with 1-Click Segmented Tabs */}
      <Box
        sx={{
          px: 2,
          py: 1,
          backgroundColor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {tabs.map((tab) => {
            const isSelected = activeTab === tab.key;
            return (
              <Box
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.875,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.15s ease',
                  backgroundColor: isSelected ? '#0F172A' : '#FFFFFF',
                  color: isSelected ? '#FFFFFF' : '#475569',
                  border: '1px solid',
                  borderColor: isSelected ? '#0F172A' : '#CBD5E1',
                  boxShadow: isSelected ? '0 1px 3px rgba(15, 23, 42, 0.15)' : 'none',
                  '&:hover': {
                    backgroundColor: isSelected ? '#1E293B' : '#F1F5F9',
                    borderColor: isSelected ? '#1E293B' : '#94A3B8',
                  },
                }}
              >
                {tab.icon}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: '0.78rem',
                    color: 'inherit',
                  }}
                >
                  {tab.label}
                </Typography>
                <Chip
                  label={tab.count}
                  size="small"
                  sx={{
                    height: 18,
                    minWidth: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.18)' : '#F1F5F9',
                    color: isSelected ? '#FFFFFF' : '#334155',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Box>
            );
          })}
        </Stack>

        {/* Contextual 1-Click Add Button */}
        <RequestPermissionTooltip
          hasPermission={hasAddPermission}
          requiredPermission={{
            action: PermissionActionEnum.Create,
            entity: currentTabConfig.permissionEntity,
          }}
          checkRequestExists={checkRequestExists}
          onRequest={requestPermission}
        >
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlined fontSize="small" />}
              disabled={!hasAddPermission}
              onClick={handlePrimaryAddClick}
              sx={{
                height: 32,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                backgroundColor: '#1D4ED8',
                boxShadow: 'none',
                px: 1.5,
                '&:hover': {
                  backgroundColor: '#1E40AF',
                  boxShadow: 'none',
                },
              }}
            >
              {currentTabConfig.addButtonLabel}
            </Button>
          </span>
        </RequestPermissionTooltip>
      </Box>

      {/* Active Register Data Table */}
      <Box sx={{ flex: 1, minHeight: 420, minWidth: 0, width: '100%', overflowX: 'auto' }}>
        {activeTab === 'people' && (
          <EssealDataTable
            height={520}
            rows={peopleRows}
            columns={peopleRegisterColumnsV2(timeFormat)}
            loading={loadingPeople}
            maxVisibleActions={2}
            rowActions={() => [
              {
                label: 'Edit',
                icon: <EditOutlined fontSize="small" />,
                tooltip: 'Edit Schedule',
                onClick: (row) => {
                  if (
                    can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })
                  ) {
                    setSelectedSchedule(row);
                  }
                },
              },
              {
                label: 'Delete',
                icon: <DeleteOutline fontSize="small" color="error" />,
                tooltip: 'Remove Schedule',
                onClick: (row) => handleDeleteSchedule(row.id, CompanyResourceResourceTypeEnum.Human, 'People'),
              },
            ]}
            getRowClassName={(row) =>
              row.companyResource?.user?.defaultEmploymentStatus === EmploymentStatusEnum.Pending ? 'scheduled-row' : ''
            }
          />
        )}

        {activeTab === 'equipment' && (
          <EssealDataTable
            height={520}
            rows={equipmentRows}
            columns={equipmentRegisterColumnsV2(timeFormat)}
            loading={loadingEquipment}
            maxVisibleActions={3}
            rowActions={(row) => [
              {
                label: 'Edit',
                icon: <EditOutlined fontSize="small" />,
                tooltip: 'Edit Schedule',
                onClick: (r) => {
                  if (
                    can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })
                  ) {
                    setSelectedSchedule(r);
                  }
                },
              },
              {
                label: 'Delete',
                icon: <DeleteOutline fontSize="small" color="error" />,
                tooltip: 'Remove Schedule',
                onClick: (r) => handleDeleteSchedule(r.id, CompanyResourceResourceTypeEnum.Equipment, 'Equipment'),
              },
              {
                label: 'Ready for Collection',
                icon: <DeliveryIcon sx={{ fontSize: '20px' }} color={row.collectable ? '#D97706' : '#CBD5E1'} />,
                tooltip: row.collectable ? 'Mark as Ready for Collection' : 'Not Collectable',
                disabled: !row.collectable,
                onClick: () => handleReadyForCollection(row.id, row.companyResource?.id as string),
              },
            ]}
          />
        )}

        {activeTab === 'subcontractors' && (
          <EssealDataTable
            height={520}
            rows={subContractorRows}
            columns={subContractorRegisterColumnsV2(timeFormat)}
            loading={loadingSubContractors}
            maxVisibleActions={2}
            rowActions={() => [
              {
                label: 'Edit',
                icon: <EditOutlined fontSize="small" />,
                tooltip: 'Edit Schedule',
                onClick: (row) => {
                  if (
                    can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })
                  ) {
                    setSelectedSchedule(row);
                  }
                },
              },
              {
                label: 'Delete',
                icon: <DeleteOutline fontSize="small" color="error" />,
                tooltip: 'Remove Schedule',
                onClick: (row) =>
                  handleDeleteSchedule(row.id, CompanyResourceResourceTypeEnum.SubContractor, 'Subcontractor'),
              },
            ]}
          />
        )}

        {activeTab === 'diary' && (
          <EssealDataTable
            height={520}
            rows={issues}
            columns={diaryEntryColumnsV2(handleUpdateDiaryInline)}
            pagination={true}
            pageSize={25}
            rowActions={(row) => [
              {
                label: 'Edit',
                icon: <EditOutlined fontSize="small" color="primary" />,
                tooltip: 'Inspect & Edit Entry',
                onClick: () => onSelectDiaryIssue(row),
              },
              {
                label: 'Delete',
                icon: <DeleteOutline fontSize="small" color="error" />,
                tooltip: 'Delete Entry',
                onClick: () => {
                  if (canPerformAction(PermissionActionEnum.Manage, PermissionEntityEnum.Reports)) {
                    removeIssue({ variables: { issueId: row.id } });
                  }
                },
              },
            ]}
          />
        )}
      </Box>

      {/* Modals */}
      {openScheduleModalType && (
        <NewScheduleModal
          addresses={selectedProject?.addresses || []}
          resourceType={openScheduleModalType}
          reportDate={selectedReport.reportDate}
          projectId={selectedProject?.id as string}
          reportId={selectedReport?.id as string}
          projectShifts={projectShifts}
          projectHolidays={projectHolidays}
          handleClose={() => setOpenScheduleModalType(null)}
          handleSuccess={(response: CreateSingleScheduleMutation) => {
            if (response.createSingleSchedule.hasConflicts) {
              setConflictingId(response.createSingleSchedule.scheduleId);
            }
          }}
        />
      )}

      {selectedSchedule && (
        <EditScheduleModal handleClose={() => setSelectedSchedule(null)} selectedSchedule={selectedSchedule} />
      )}

      {conflictingId && (
        <ConflictDetectedModal
          conflictingId={conflictingId}
          handleClose={() => setConflictingId(null)}
          handleCancel={() => {
            setConflictingId(null);
            setOpenScheduleModalType(CompanyResourceResourceTypeEnum.Human);
          }}
        />
      )}

      {openDiaryModal && <DiaryEntryModal handleClose={() => setOpenDiaryModal(false)} />}
    </Paper>
  );
};

export default UnifiedRegistersCardV2;
